import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, ArrowRight, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import AuthBrandShowcase from './AuthBrandShowcase';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LEGAL_BASE = 'https://darkplanner.com.br';

// Textura de grão (igual ao login / corpo da landing).
const GRAIN_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='220'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

const ForgotPassword = () => {
  usePageTitle('Recuperar Senha');
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('forgot.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleFocus = (e) => {
    e.target.parentElement.style.borderColor = '#0DAEFF';
    e.target.parentElement.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)';
  };
  const handleBlur = (e) => {
    e.target.parentElement.style.borderColor = 'rgba(255,255,255,0.08)';
    e.target.parentElement.style.boxShadow = 'none';
  };

  return (
    <div className="dp-fp-page" style={{
      minHeight: '100vh', position: 'relative',
      background: '#0A0A0A',
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      overflow: 'hidden',
    }}>
      {/* Fundo: grão */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN_URI, opacity: 0.04 }} />
      </div>

      <div className="dp-fp-shell">
        {/* ─────── PAINEL DE MARCA ─────── */}
        <aside className="dp-fp-brand">
          <div className="dp-fp-glow" />
          <div className="dp-fp-brand-inner">
            <div className="dp-fp-logo">
              <img src="/logo-darkplanner.png" alt="Dark Planner" />
              <span>Dark Planner</span>
            </div>
            <h1 className="dp-fp-brand-title">{t('login.slogan')}</h1>
            <AuthBrandShowcase />
            <div className="dp-fp-trusted">{t('login.trusted_by')}</div>
          </div>
        </aside>

        {/* ─────── PAINEL DO FORMULÁRIO ─────── */}
        <main className="dp-fp-form">
          <div className="dp-fp-card">
            <div className="dp-fp-card-logo">
              <img src="/logo-darkplanner.png" alt="Dark Planner" />
            </div>

            {success ? (
              <>
                <div className="dp-fp-success-icon"><CheckCircle size={30} style={{ color: '#10B981' }} /></div>
                <h2 className="dp-fp-title" style={{ textAlign: 'center' }}>{t('forgot.success_title')}</h2>
                <p className="dp-fp-sub" style={{ textAlign: 'center' }}>
                  {t('forgot.success_desc', { email })}
                </p>
                <p style={{ textAlign: 'center', fontSize: '12.5px', color: '#8A8A8A', margin: '0 0 22px 0' }}>
                  {t('forgot.success_expire')}
                </p>
                <button className="dp-fp-cta" onClick={() => navigate('/login')}>
                  {t('forgot.success_btn')}
                  <ArrowRight size={15} />
                </button>
              </>
            ) : (
              <>
                <h2 className="dp-fp-title">{t('forgot.title')}</h2>
                <p className="dp-fp-sub">{t('forgot.subtitle')}</p>

                {error && (
                  <div style={{
                    marginBottom: '16px', padding: '11px 14px', borderRadius: '12px',
                    background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                  }}>
                    <AlertCircle size={16} style={{ color: '#EF4444', flexShrink: 0, marginTop: '1px' }} />
                    <p style={{ fontSize: '12.5px', color: '#EF4444', margin: 0, lineHeight: 1.5 }}>{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div>
                    <label className="dp-fp-label">{t('login.email')}</label>
                    <div className="dp-fp-input-wrap">
                      <Mail size={16} style={{ color: '#6B7280', flexShrink: 0 }} />
                      <input
                        type="email" required value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={handleFocus} onBlur={handleBlur}
                        placeholder={t('login.email_placeholder')}
                        className="dp-fp-input"
                      />
                    </div>
                  </div>

                  <button type="submit" disabled={loading} className="dp-fp-cta" style={{ marginTop: '4px', opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                    {loading ? (
                      <><Loader2 size={16} style={{ animation: 'dp-fp-spin 1s linear infinite' }} />{t('forgot.btn_sending')}</>
                    ) : (
                      <>{t('forgot.btn_send')}<ArrowRight size={15} /></>
                    )}
                  </button>
                </form>

                {/* Voltar para login */}
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <button type="button" onClick={() => navigate('/login')} className="dp-fp-back">
                    <ArrowLeft size={15} />
                    {t('forgot.back_to_login')}
                  </button>
                </div>

                {/* Dica */}
                <div className="dp-fp-tip">
                  {t('forgot.tip')}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Rodapé da página */}
      <footer className="dp-fp-foot">
        <span>{t('login.copyright')}</span>
        <span className="dp-fp-foot-links">
          <a href={`${LEGAL_BASE}/privacy`} target="_blank" rel="noopener noreferrer">{t('login.privacy_policy')}</a>
          <span style={{ opacity: 0.4 }}>·</span>
          <a href={`${LEGAL_BASE}/terms`} target="_blank" rel="noopener noreferrer">{t('login.terms_of_service')}</a>
        </span>
      </footer>

      <style>{`
        @keyframes dp-fp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .dp-fp-page input::placeholder { color: #4B5563 !important; }

        .dp-fp-shell { display: flex; min-height: 100vh; position: relative; z-index: 10; }
        .dp-fp-brand, .dp-fp-form {
          flex: 1; display: flex; align-items: center; justify-content: center;
          padding: 40px; box-sizing: border-box;
        }
        .dp-fp-brand { position: relative; overflow: hidden; }
        .dp-fp-form { overflow-y: auto; padding-bottom: 88px; }

        .dp-fp-glow {
          position: absolute; top: 50%; left: 50%; width: 120%; height: 80%;
          transform: translate(-50%, -50%);
          background: radial-gradient(ellipse, rgba(13,174,255,0.18) 0%, rgba(59,130,246,0.06) 40%, transparent 70%);
          filter: blur(60px); pointer-events: none;
        }
        .dp-fp-brand-inner { position: relative; max-width: 420px; width: 100%; }
        .dp-fp-logo { display: inline-flex; align-items: center; gap: 12px; margin-bottom: 28px; }
        .dp-fp-logo img { width: 44px; height: 44px; border-radius: 12px; object-fit: cover; box-shadow: 0 8px 32px rgba(13,174,255,0.25); }
        .dp-fp-logo span { font-size: 22px; font-weight: 700; color: #F5F5F5; letter-spacing: -0.4px; }
        .dp-fp-brand-title { font-size: 34px; font-weight: 700; color: #F5F5F5; line-height: 1.12; letter-spacing: -1px; margin: 0 0 24px 0; }
        .dp-fp-features { list-style: none; padding: 0; margin: 0 0 28px 0; display: flex; flex-direction: column; gap: 13px; }
        .dp-fp-features li { display: flex; align-items: center; gap: 10px; font-size: 14.5px; color: #B8B8B8; }
        .dp-fp-trusted { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 600; color: #0DAEFF; padding: 6px 13px; border-radius: 999px; background: rgba(13,174,255,0.08); border: 1px solid rgba(13,174,255,0.22); }

        .dp-fp-card { width: 100%; max-width: 400px; }
        .dp-fp-card-logo { display: none; justify-content: center; margin-bottom: 14px; }
        .dp-fp-card-logo img { width: 52px; height: 52px; border-radius: 14px; object-fit: cover; box-shadow: 0 8px 32px rgba(13,174,255,0.25); }
        .dp-fp-success-icon { width: 60px; height: 60px; border-radius: 50%; background: rgba(16,185,129,0.10); border: 1px solid rgba(16,185,129,0.25); display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
        .dp-fp-title { font-size: 24px; font-weight: 700; color: #F5F5F5; margin: 0 0 4px 0; letter-spacing: -0.5px; }
        .dp-fp-sub { font-size: 14px; color: #8A8A8A; margin: 0 0 24px 0; line-height: 1.5; }
        .dp-fp-label { display: block; font-size: 12px; font-weight: 600; color: #8A8A8A; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
        .dp-fp-input-wrap { display: flex; align-items: center; gap: 0; background: rgba(10,10,10,0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 0 12px; transition: border-color 0.2s, box-shadow 0.2s; }
        .dp-fp-input { flex: 1; background: transparent; border: none; padding: 13px 8px; color: #F5F5F5; font-size: 14px; outline: none; font-family: 'Outfit', sans-serif; }

        .dp-fp-cta { width: 100%; padding: 14px; background: linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%); color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s cubic-bezier(0.4,0,0.2,1); display: flex; align-items: center; justify-content: center; gap: 8px; letter-spacing: 0.2px; font-family: 'Outfit', sans-serif; }
        .dp-fp-cta:hover:not(:disabled) { background: linear-gradient(135deg, #1FB8FF 0%, #4F8FF8 100%); transform: translateY(-1px); }
        .dp-fp-cta svg:last-child { transition: transform 0.2s cubic-bezier(0.4,0,0.2,1); }
        .dp-fp-cta:hover:not(:disabled) svg:last-child { transform: rotate(-45deg); }

        .dp-fp-back { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; color: #0DAEFF; cursor: pointer; font-size: 13px; font-weight: 600; font-family: 'Outfit', sans-serif; padding: 6px; }
        .dp-fp-tip { margin-top: 20px; padding: 12px 14px; border-radius: 12px; background: rgba(13,174,255,0.07); border: 1px solid rgba(13,174,255,0.18); font-size: 12.5px; color: #9CC9DD; line-height: 1.5; }

        .dp-fp-foot { position: absolute; bottom: 0; left: 0; right: 0; z-index: 12; display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; padding: 14px 20px; font-size: 11.5px; color: #555; }
        .dp-fp-foot-links { display: inline-flex; align-items: center; gap: 8px; }
        .dp-fp-foot a { color: #777; text-decoration: none; transition: color 200ms ease; }
        .dp-fp-foot a:hover { color: #0DAEFF; }

        @media (max-width: 900px) {
          .dp-fp-shell { flex-direction: column; }
          .dp-fp-brand { order: 1; padding: 56px 22px 20px; }
          .dp-fp-form { order: 2; flex: 1; align-items: flex-start; padding: 8px 22px 76px; }
          .dp-fp-brand-title { font-size: 24px; margin-bottom: 18px; }
          .dp-fp-features { display: none; }
        }
        @media (max-width: 520px) {
          .dp-fp-brand { display: none; }
          .dp-fp-form { padding: 64px 18px 76px; }
          .dp-fp-card-logo { display: flex; }
          .dp-fp-title { text-align: center; }
          .dp-fp-sub { text-align: center; }
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;
